import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes, css } from 'styled-components'
import { GhostButton, Pill, PrimaryButton, Row } from '../../../layout/styledComponents'
import { parseUtcDate } from '../../../utils/dates'
import {
  getSessionProgressState,
  progressMarkerLabel,
} from '../../../utils/sessionProgress'
import { usePatient } from '../../../context/PatientContext'
import { useReceiptDrawer } from '../../../hooks/useReceiptDrawer'
import ReceiptDrawer from '../../../components/ReceiptDrawer'

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(107, 144, 128, 0.45); }
  70%  { box-shadow: 0 0 0 12px rgba(107, 144, 128, 0); }
  100% { box-shadow: 0 0 0 0 rgba(107, 144, 128, 0); }
`

const Card = styled.article`
  display: flex;
  align-items: stretch;
  gap: 16px;
  padding: 18px 20px;
  background: ${(p) =>
    p.$progressState === 'IN_PROGRESS'
      ? p.theme?.color?.accentSoft || '#E1ECE7'
      : p.theme?.color?.bgRaised || '#fff'};
  border: 1px solid
    ${(p) =>
      p.$progressState === 'IN_PROGRESS'
        ? p.theme?.color?.accent || '#6B9080'
        : p.$progressState === 'STARTING_SOON'
          ? p.theme?.color?.primary || '#1F3A5F'
          : p.$progressState === 'RECENTLY_ENDED'
            ? p.theme?.color?.borderStrong || '#D5CCBC'
            : p.theme?.color?.border || '#E6E0D6'};
  border-radius: 16px;
  box-shadow: ${(p) => p.theme?.shadow?.card};
  ${(p) =>
    p.$progressState === 'IN_PROGRESS' &&
    css`
      animation: ${pulse} 2.4s ease-out infinite;
    `}
  @media (max-width: 540px) {
    flex-wrap: wrap;
  }
`

// Practitioner avatar — past sessions get a richer "who you saw" anchor at
// the left of the card. Falls back to initials chip when no profile pic.
const Avatar = styled.span`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => p.theme?.color?.primarySoft || '#E8EEF6'};
  color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  letter-spacing: 0.04em;
  overflow: hidden;
`

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const When = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
  min-width: 90px;
`

const WhenLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

const WhenTime = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
`

const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const HeadlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
`

const Sub = styled.div`
  font-size: 13px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

// Chip strip for the structured info on past cards: duration, mode,
// session type, payment summary. Each chip is small and visually muted so
// the card reads scanned-not-paragraphed.
const ChipStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  background: ${(p) =>
    p.$tone === 'success'
      ? p.theme?.color?.accentSoft || '#E1ECE7'
      : p.$tone === 'urgent'
        ? p.theme?.color?.urgentSoft || '#FBEAE9'
        : p.theme?.color?.bgSubtle || '#F4F0E9'};
  color: ${(p) =>
    p.$tone === 'success'
      ? p.theme?.color?.accent || '#3F7D58'
      : p.$tone === 'urgent'
        ? p.theme?.color?.urgent || '#C24D49'
        : p.theme?.color?.inkSoft || '#4B5563'};
`

const FeedbackBlock = styled.div`
  margin-top: 6px;
  padding: 8px 12px;
  border-left: 3px solid ${(p) => p.theme?.color?.accent || '#6B9080'};
  background: ${(p) => p.theme?.color?.accentSoft || '#E1ECE7'};
  border-radius: 0 10px 10px 0;
  font-size: 12px;
  color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
`

const Stars = styled.div`
  letter-spacing: 1px;
  font-size: 14px;
  color: ${(p) => p.theme?.color?.warning || '#B97A2A'};
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
  @media (max-width: 540px) {
    align-items: stretch;
    width: 100%;
  }
`

// Marker pill for the current progress state (upcoming variant only).
const MarkerPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${(p) =>
    p.$tone === 'success'
      ? p.theme?.color?.accentSoft || '#E1ECE7'
      : p.$tone === 'primary'
        ? p.theme?.color?.primarySoft || '#E8EEF6'
        : p.theme?.color?.bgSubtle || '#F4F0E9'};
  color: ${(p) =>
    p.$tone === 'success'
      ? p.theme?.color?.accent || '#6B9080'
      : p.$tone === 'primary'
        ? p.theme?.color?.primary || '#1F3A5F'
        : p.theme?.color?.inkMuted || '#6B7280'};
  &::before {
    ${(p) =>
      p.$tone === 'success' &&
      css`
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${p.theme?.color?.accent || '#6B9080'};
      `}
  }
`

const StatusPill = styled.span`
  display: inline-flex;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${(p) =>
    p.$tone === 'urgent'
      ? p.theme?.color?.urgentSoft || '#FBEAE9'
      : p.$tone === 'success'
        ? p.theme?.color?.accentSoft || '#E1ECE7'
        : p.theme?.color?.bgSubtle || '#F4F0E9'};
  color: ${(p) =>
    p.$tone === 'urgent'
      ? p.theme?.color?.urgent || '#C24D49'
      : p.$tone === 'success'
        ? p.theme?.color?.accent || '#3F7D58'
        : p.theme?.color?.inkMuted || '#6B7280'};
`

function whenLabel(iso) {
  if (!iso) return { label: '', time: '' }
  const d = parseUtcDate(iso)
  const now = new Date()
  const today = d.toDateString() === now.toDateString()
  const tomorrow = parseUtcDate(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const isTomorrow = d.toDateString() === tomorrow.toDateString()
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  if (today) return { label: 'Today', time }
  if (isTomorrow) return { label: 'Tomorrow', time }
  return {
    label: d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }),
    time,
  }
}

function fullDateLabel(iso) {
  if (!iso) return ''
  const d = parseUtcDate(iso)
  const now = new Date()
  return d.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  })
}

function initials(name) {
  if (!name) return '·'
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '·'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

function formatMinor(amountMinor, currency) {
  if (amountMinor == null || amountMinor <= 0) return null
  const major = amountMinor / 100
  const cur = (currency || 'INR').toUpperCase()
  if (cur === 'INR') return `₹${major.toLocaleString('en-IN')}`
  if (cur === 'USD') return `$${major.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  return `${cur} ${major}`
}

function pastStatusTone(status) {
  const s = String(status || '').toUpperCase()
  if (s === 'CANCELLED') return 'urgent'
  if (s === 'COMPLETED') return 'success'
  if (s === 'NO_SHOW') return 'urgent'
  return 'default'
}

function pastStatusLabel(status) {
  const s = String(status || '').toUpperCase()
  if (s === 'CANCELLED') return 'Cancelled'
  if (s === 'COMPLETED') return 'Completed'
  if (s === 'NO_SHOW') return 'No-show'
  return s.replace('_', ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
}

function modeLabel(mode) {
  if (mode === 'OFFLINE') return 'In-clinic'
  if (mode === 'ONLINE') return 'Video call'
  return null
}

function PractitionerAvatar({ avatarUrl, name }) {
  const [broken, setBroken] = useState(false)
  if (avatarUrl && !broken) {
    return (
      <Avatar>
        <AvatarImg src={avatarUrl} alt="" onError={() => setBroken(true)} />
      </Avatar>
    )
  }
  return <Avatar>{initials(name)}</Avatar>
}

export default function SessionCard({
  session,
  variant = 'upcoming', // 'upcoming' | 'past'
  onReschedule,
  onCancel,
  onFeedback,
}) {
  const navigate = useNavigate()
  const { currentProfile } = usePatient()
  const receiptDrawer = useReceiptDrawer()
  const downloading =
    receiptDrawer.state.loading && receiptDrawer.state.eventId === session?.eventId
  const downloadError =
    receiptDrawer.state.error && receiptDrawer.state.eventId === session?.eventId
      ? receiptDrawer.state.error
      : null

  if (!session) return null
  const w = whenLabel(session.scheduledAt)
  const canJoin = variant === 'upcoming' && !!session.canJoinNow
  const progress = getSessionProgressState(session)
  const markerLabel = variant === 'upcoming' ? progressMarkerLabel(progress) : null
  const markerTone =
    progress === 'IN_PROGRESS'
      ? 'success'
      : progress === 'STARTING_SOON'
        ? 'primary'
        : progress === 'RECENTLY_ENDED'
          ? 'muted'
          : null

  const paid = formatMinor(session.paidAmountMinor, session.currency)
  const original = formatMinor(session.originalPriceMinor, session.currency)
  const showOriginal =
    paid && original && session.originalPriceMinor > session.paidAmountMinor
  const mode = modeLabel(session.sessionMode)

  function handleDownloadReceipt() {
    if (!currentProfile || !session.eventId) return
    receiptDrawer.open(currentProfile, session)
  }

  return (
    <Card $progressState={progress}>
      {variant === 'past' && (
        <PractitionerAvatar
          avatarUrl={session.practitionerAvatarUrl}
          name={session.practitionerName}
        />
      )}
      <When>
        <WhenLabel>{w.label}</WhenLabel>
        <WhenTime>{w.time}</WhenTime>
      </When>
      <Body>
        <HeadlineRow>
          <Title>{session.title || 'Session'}</Title>
          {variant === 'upcoming' && markerLabel ? (
            <MarkerPill $tone={markerTone}>{markerLabel}</MarkerPill>
          ) : null}
          {variant === 'past' ? (
            <StatusPill $tone={pastStatusTone(session.status)}>
              {pastStatusLabel(session.status)}
            </StatusPill>
          ) : null}
        </HeadlineRow>
        <Sub>
          {session.practitionerName}
          {session.practitionerSpecialty ? ` · ${session.practitionerSpecialty}` : ''}
        </Sub>
        {variant === 'past' ? (
          <>
            <Sub>{fullDateLabel(session.scheduledAt)}</Sub>
            <ChipStrip>
              {session.durationMinutes ? <Chip>{session.durationMinutes} min</Chip> : null}
              {mode ? <Chip>{mode}</Chip> : null}
              {session.locationName ? <Chip>📍 {session.locationName}</Chip> : null}
              {paid ? (
                <Chip $tone="success">
                  {showOriginal ? (
                    <>
                      Paid {paid} <span style={{ textDecoration: 'line-through', opacity: 0.6, marginLeft: 4 }}>
                        {original}
                      </span>
                    </>
                  ) : (
                    <>Paid {paid}</>
                  )}
                  {session.paymentMode ? ` · ${session.paymentMode}` : ''}
                </Chip>
              ) : null}
            </ChipStrip>
            {session.feedback ? (
              <FeedbackBlock>
                <Stars aria-label={`Rated ${session.feedback.rating} of 5`}>
                  {'★'.repeat(session.feedback.rating || 0)}
                  {'☆'.repeat(Math.max(0, 5 - (session.feedback.rating || 0)))}
                </Stars>
                {session.feedback.text ? (
                  <div style={{ marginTop: 4 }}>&ldquo;{session.feedback.text}&rdquo;</div>
                ) : null}
              </FeedbackBlock>
            ) : null}
            {downloadError ? (
              <Sub style={{ color: '#C24D49' }}>{downloadError}</Sub>
            ) : null}
          </>
        ) : (
          <Sub>
            {session.durationMinutes ? `${session.durationMinutes} min` : ''} · {session.sessionType}
          </Sub>
        )}
      </Body>
      <Actions>
        {variant === 'upcoming' ? (
          <>
            {canJoin ? (
              <PrimaryButton
                onClick={() =>
                  navigate(
                    session.joinUrl ||
                      (session.slug ? `/l/${session.slug}` : `/l/${session.eventId}`),
                  )
                }
              >
                Join
              </PrimaryButton>
            ) : (
              <GhostButton
                onClick={() => navigate(`/patient-home/sessions/${session.eventId}`)}
              >
                View
              </GhostButton>
            )}
            {session.canManage && (
              <Row $gap="6px">
                <button
                  onClick={() => onReschedule && onReschedule(session)}
                  style={btnLink}
                >
                  Request reschedule
                </button>
                <span style={{ color: '#D5CCBC' }}>·</span>
                <button onClick={() => onCancel && onCancel(session)} style={btnLink}>
                  Request cancel
                </button>
              </Row>
            )}
          </>
        ) : (
          <>
            {session.receiptAvailable ? (
              <PrimaryButton
                onClick={handleDownloadReceipt}
                disabled={downloading}
                title="Download receipt as PDF"
              >
                {downloading ? 'Preparing…' : '⬇  Receipt'}
              </PrimaryButton>
            ) : null}
            {!session.feedback && onFeedback ? (
              <GhostButton onClick={() => onFeedback(session)}>
                {session.status?.toUpperCase() === 'COMPLETED' ? 'Rate session' : 'Rate'}
              </GhostButton>
            ) : null}
            <button
              onClick={() => navigate(`/patient-home/sessions/${session.eventId}`)}
              style={btnLink}
            >
              View details →
            </button>
          </>
        )}
      </Actions>
      <ReceiptDrawer
        open={receiptDrawer.state.open}
        title={receiptDrawer.state.title || 'Receipt'}
        subtitle={receiptDrawer.state.subtitle}
        blobUrl={receiptDrawer.state.blobUrl}
        mimeType={receiptDrawer.state.mimeType}
        filename={receiptDrawer.state.filename}
        fallbackText={receiptDrawer.state.fallbackText}
        onClose={receiptDrawer.close}
      />
    </Card>
  )
}

const btnLink = {
  background: 'transparent',
  border: 'none',
  color: '#6B7280',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  padding: 0,
}
